import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard,
  Crown,
  Star,
  Shield,
  CheckCircle,
  ArrowUpRight,
} from "@/components/icons";
import { formatCurrency, formatNumber } from "@/utils/formatters";
import { ICON_COLORS } from "@/constants/ui";
import type { CreditPlan } from "@/types/credits";

interface CreditPlansProps {
  plans: CreditPlan[];
  purchasingPlan: string | null;
  onPurchase: (planId: string) => void;
}

const PLAN_ICONS = [Shield, Star, Crown];

const PLAN_ICON_COLORS = [
  ICON_COLORS.slate,
  ICON_COLORS.blue,
  ICON_COLORS.purple,
];

export const CreditPlans: React.FC<CreditPlansProps> = React.memo(
  ({ plans, purchasingPlan, onPurchase }) => {
    return (
      <Card className="glassmorphism shadow-lg">
        <CardHeader className="pb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg">
              <CreditCard className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Credit Plans
            </CardTitle>
          </div>
          <p className="text-base text-muted-foreground">
            Choose a plan that fits your messaging needs
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const isPopular = plan.id === "pro";
              const IconComponent = PLAN_ICONS[index];

              return (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden glassmorphism shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    plan.isActive ? "ring-2 ring-primary ring-offset-2" : ""
                  }`}
                >
                  {plan.isActive && (
                    <Badge className="absolute -top-2 right-4 bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg">
                      <Crown className="h-3 w-3 mr-1" />
                      Current Plan
                    </Badge>
                  )}
                  {isPopular && !plan.isActive && (
                    <Badge className="absolute -top-2 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg">
                      <Star className="h-3 w-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-16 translate-x-16"></div>

                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl font-bold text-foreground">
                        {plan.name}
                      </CardTitle>
                      <IconComponent
                        className={`h-5 w-5 ${PLAN_ICON_COLORS[index]}`}
                      />
                    </div>
                    <div className="text-lg">
                      <span className="text-3xl font-bold text-foreground">
                        {formatCurrency(plan.price)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        /{plan.duration} days
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="text-center p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg">
                      <div className="text-4xl font-bold text-primary mb-1">
                        {formatNumber(plan.credits)}
                      </div>
                      <div className="text-sm text-muted-foreground font-medium">
                        Credits
                      </div>
                    </div>

                    <div className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center text-sm"
                        >
                          <CheckCircle className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                          <span className="text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className={`w-full h-12 text-base font-semibold transition-all duration-200 ${
                        plan.isActive
                          ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                          : isPopular
                          ? "bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
                          : "bg-gradient-to-r from-secondary to-secondary/80 hover:from-secondary/90 hover:to-secondary/70"
                      }`}
                      disabled={plan.isActive || purchasingPlan === plan.id}
                      onClick={() => onPurchase(plan.id)}
                    >
                      {purchasingPlan === plan.id ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing...
                        </div>
                      ) : plan.isActive ? (
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Current Plan
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <ArrowUpRight className="h-4 w-4 mr-2" />
                          Purchase Plan
                        </div>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }
);
